import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { AppLayout } from '@/components/AppLayout'
import { AuthGuard } from '@/features/auth'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageSkeleton } from '@/components/PageSkeleton'

const FleetOverviewPage = lazy(() => import('@/pages/FleetOverviewPage'))
const DetectionsListPage = lazy(() => import('@/pages/DetectionsListPage'))
const DetectionDetailPage = lazy(() => import('@/pages/DetectionDetailPage'))
const ArtifactViewerPage = lazy(() => import('@/pages/ArtifactViewerPage'))
const DevicesListPage = lazy(() => import('@/pages/DevicesListPage'))
const DeviceDetailPage = lazy(() => import('@/pages/DeviceDetailPage'))
const RulesListPage = lazy(() => import('@/pages/RulesListPage'))
const RuleDetailPage = lazy(() => import('@/pages/RuleDetailPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const SignInPage = lazy(() => import('@/pages/SignInPage'))

function Suspended({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: (
      <Suspended>
        <SignInPage />
      </Suspended>
    ),
  },
  {
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: (
      <ErrorBoundary>
        <div />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspended>
            <FleetOverviewPage />
          </Suspended>
        ),
      },
      {
        path: 'detections',
        element: (
          <Suspended>
            <DetectionsListPage />
          </Suspended>
        ),
      },
      {
        path: 'detections/:id',
        element: (
          <Suspended>
            <DetectionDetailPage />
          </Suspended>
        ),
      },
      {
        path: 'detections/:id/artifact',
        element: (
          <Suspended>
            <ArtifactViewerPage />
          </Suspended>
        ),
      },
      {
        path: 'devices',
        element: (
          <Suspended>
            <DevicesListPage />
          </Suspended>
        ),
      },
      {
        path: 'devices/:id',
        element: (
          <Suspended>
            <DeviceDetailPage />
          </Suspended>
        ),
      },
      {
        path: 'rules',
        element: (
          <Suspended>
            <RulesListPage />
          </Suspended>
        ),
      },
      {
        path: 'rules/:id',
        element: (
          <Suspended>
            <RuleDetailPage />
          </Suspended>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspended>
            <SettingsPage />
          </Suspended>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
